import objectPath from 'object-path';

import { routerAsync } from '@/app-module';
import { i18n } from '@shared/i18n';

import Errors from '@shared/error/errors';
import Message from '@shared/message/message';

import { StripeConnectedAccountService } from '@modules/stripe/stripe-connected-account/stripe-connected-account-service';
import connectedAccountCountryBySubpropertyCountry from '@enums/connectedAccountCountryBySubpropertyCountry.json';

const defaultRecord = {
  businessType: require('@enums/businessType.json')[0],
};

export default {
  namespaced: true,

  state: {
    findLoading: false,
    saveLoading: false,
    record: defaultRecord,
    globalErrorMessage: null,
    fieldErrors: {},
  },

  getters: {
    record: (state, getters, rootState, rootGetters) => {
      const currentManage = rootGetters['auth/manage/currentManage']
      const tosAccepted =
        'tosAcceptance' in state.record &&
        'date' in state.record.tosAcceptance &&
        'ip' in state.record.tosAcceptance

      const { country: subpropertyCountry } = currentManage.subproperty
      const country = subpropertyCountry in connectedAccountCountryBySubpropertyCountry
        ? connectedAccountCountryBySubpropertyCountry[subpropertyCountry]
        : 'US'

      return {
        ...state.record,
        subpropertyName: currentManage.subproperty.name,
        country,
        tosAccepted,
        address: {
          ...state.record.address,
          country,
        },
      }
    },
    globalErrorMessage: (state) => state.globalErrorMessage,
    fieldErrors: (state) => state.fieldErrors,
    findLoading: (state) => !!state.findLoading,
    saveLoading: (state) => !!state.saveLoading,
  },

  mutations: {
    RESET(state) {
      state.findLoading = false;
      state.saveLoading = false;
      state.record = defaultRecord;
      state.globalErrorMessage = null;
      state.fieldErrors = {};
    },

    FIND_STARTED(state) {
      state.record = defaultRecord;
      state.findLoading = true;
    },

    FIND_SUCCESS(state, payload) {
      state.record = payload;
      state.findLoading = false;
    },

    FIND_ERROR(state) {
      state.record = defaultRecord;
      state.findLoading = false;
    },

    CREATE_STARTED(state) {
      state.saveLoading = true;
      state.globalErrorMessage = null;
      state.fieldErrors = {};
    },

    CREATE_SUCCESS(state) {
      state.saveLoading = false;
      state.globalErrorMessage = null;
      state.fieldErrors = {};
    },

    UPDATE_STARTED(state) {
      state.saveLoading = true;
      state.globalErrorMessage = null;
      state.fieldErrors = {};
    },

    UPDATE_SUCCESS(state) {
      state.saveLoading = false;
      state.globalErrorMessage = null;
    },

    SET_ERROR(state, { errorMessage, errors }) {
      state.saveLoading = false;

      state.globalErrorMessage = null;
      state.fieldErrors = [];

      state.globalErrorMessage = errorMessage;
      state.fieldErrors = errors;
    },

    CLEAR_FIELD_ERRORS(state, { fieldNames }) {
      for (const fieldName of fieldNames) {
        objectPath.del(state.fieldErrors, fieldName);
      }
      state.fieldErrors = Object.assign({}, state.fieldErrors);
      if (!Object.keys(state.fieldErrors).length) state.globalErrorMessage = null;
    }
  },

  actions: {
    doNew({ commit, rootGetters }) {
      commit('RESET');

      const currentManage = rootGetters['auth/manage/currentManage']
      const manageConnectedAccountId = 'subproperty' in currentManage && currentManage.subproperty.connectedAccountId
      const paymentsEnabled = 'subproperty' in currentManage && currentManage.subproperty.paymentsEnabled

      if (!paymentsEnabled) {
        routerAsync().push({ name: 'home' })
      } else if (manageConnectedAccountId) {
        routerAsync().push({
          name: 'finantialSettings',
          params: {
            propertyId: currentManage.property.id,
            subpropertyId: currentManage.subproperty.id,
          },
        })
      }
    },

    async doFind({ commit, rootGetters }, { id, propertyId }) {
      try {
        commit('FIND_STARTED');

        const currentManage = rootGetters['auth/manage/currentManage']
        const manageConnectedAccountId = 'subproperty' in currentManage && currentManage.subproperty.connectedAccountId
        const paymentsEnabled = 'subproperty' in currentManage && currentManage.subproperty.paymentsEnabled

        if (!paymentsEnabled) {
          return routerAsync().push({ name: 'home' });
        } else if (!manageConnectedAccountId) {
          return routerAsync().push({
            name: 'finantialSettings',
            params: {
              propertyId: currentManage.property.id,
              subpropertyId: currentManage.subproperty.id,
            },
          });
        } else if (id !== manageConnectedAccountId) {
          routerAsync().push({
            params: {
              name: 'stripeConnectedAccountEdit',
              id: manageConnectedAccountId,
            },
          });
        }

        const fetchId = id !== manageConnectedAccountId ? manageConnectedAccountId : id;
        const record = await StripeConnectedAccountService.find(fetchId, propertyId);

        commit('FIND_SUCCESS', record);
      } catch (error) {
        Errors.handle(error);
        commit('FIND_ERROR');
        routerAsync().push('/');
      }
    },

    async doCreate({ commit, dispatch, rootGetters }, { propertyId, subpropertyId, values, backToSettings = false }) {
      try {
        commit('CREATE_STARTED');
        await StripeConnectedAccountService.create(propertyId, subpropertyId, values);
        commit('CREATE_SUCCESS');

        let currentManage = rootGetters['auth/manage/currentManage'];
        
        dispatch('subproperty/requirements/doFetch', {
          propertyId: currentManage.property.id,
          subpropertyId: currentManage.subproperty.id,
          userId: rootGetters['auth/currentUser'].id
        }, {
          root: true,
        });
        
        await dispatch('auth/manage/doUpdateManage', {
          manage: currentManage,
        }, {
          root: true,
        });

        currentManage = rootGetters['auth/manage/currentManage'];

        Message.success(i18n('entities.stripeConnectedAccount.create.success'));

        const goTo = backToSettings
          ? { name: 'finantialSettings', params: { propertyId, subpropertyId } }
          : { name: 'home'};

        routerAsync().push(goTo);
      } catch (error) {
        Errors.handle(error, (errorMessage, errors) => {
          commit('SET_ERROR', { errorMessage, errors });
        });
      }
    },

    async doUpdate({ commit }, { id, values, propertyId, subpropertyId, connectedAccountId, backToSettings = false }) {
      try {
        commit('UPDATE_STARTED');
        await StripeConnectedAccountService.update(id, propertyId, connectedAccountId, values);
        commit('UPDATE_SUCCESS');
        Message.success(i18n('entities.stripeConnectedAccount.update.success'));

        const goTo = backToSettings
          ? { name: 'finantialSettings', params: { propertyId, subpropertyId } }
          : { name: 'home' };
        routerAsync().push(goTo);
      } catch (error) {
        Errors.handle(error, (errorMessage, errors) => {
          commit('SET_ERROR', { errorMessage, errors });
        });
      }
    },

    doHandleStripeError({ commit }, error) {
      const wrappedError = {};

      objectPath.set(wrappedError, 'networkError.result.errors.0.code', 400);
      objectPath.set(wrappedError, 'networkError.result.errors.0.raw', error);
      objectPath.set(wrappedError, 'networkError.result.errors.0.fromStripe', true);

      Errors.handle(wrappedError, (errorMessage, errors) => {
        commit('SET_ERROR', { errorMessage, errors });
      });
    },

    doSetStripeError({ commit }, { message }) {
      commit('SET_ERROR', { errorMessage: message });
    },

    doClearStripeError({ commit }, { fieldNames }) {
      commit('CLEAR_FIELD_ERRORS', { fieldNames });
    },

    async doUpdateProvided({ commit }, { propertyId, connectedAccountId, token }) {
      commit('UPDATE_STARTED');

      const { stripeConnectedAccountId: id } = await StripeConnectedAccountService.find(connectedAccountId, propertyId);

      await StripeConnectedAccountService.update(connectedAccountId, propertyId, id, {
        accountToken: token,
      });

      commit('UPDATE_SUCCESS');
    },
  },
};
